import { useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { STAGES, ROLE_LABELS } from '@/lib/roles';
import { useAuth } from '@/lib/auth';
import { Compass, CheckSquare, FileText, Shield } from 'lucide-react';
import { useSimpleLabel } from '@/lib/prefs';

export default function WizardPage() {
  const { user } = useAuth();
  const t = useSimpleLabel('wizard');
  const [role, setRole] = useState(user.role || 'team');
  const [stage, setStage] = useState('strategy_concept');
  const [res, setRes] = useState(null);

  const run = async () => {
    try { const r = await api.post('/wizard/next', { role, stage }); setRes(r.data); }
    catch (e) { toast.error(parseErr(e)); }
  };

  return (
    <AppShell breadcrumbs={[{ label: t('welcome', 'What next? Wizard') }]}>
      <div className="p-6 max-w-[900px] mx-auto" data-testid="wizard-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))' }}>
            <Compass size={18} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-semibold">{t('welcome', 'What do I do next?')}</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))]">{t('help_text', "Pick a role and stage — we'll tell you the exact responsibilities, deliverables, QC gate, and communication rule.")}</p>
          </div>
        </div>

        <div className="mt-6 border rounded-xl bg-[hsl(var(--card))] p-4 grid grid-cols-1 md:grid-cols-3 gap-3" style={{ borderColor: 'hsl(var(--border))' }}>
          <label className="block">
            <span className="text-[12px] text-[hsl(var(--foreground-muted))]">{t('role_label', 'Role')}</span>
            <select data-testid="wizard-role" className="mt-1 w-full h-9 border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }} value={role} onChange={e => setRole(e.target.value)}>
              {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[12px] text-[hsl(var(--foreground-muted))]">{t('stage_label', 'Stage')}</span>
            <select data-testid="wizard-stage" className="mt-1 w-full h-9 border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }} value={stage} onChange={e => setStage(e.target.value)}>
              {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <div className="flex items-end">
            <Button onClick={run} className="w-full" data-testid="wizard-run">Show me</Button>
          </div>
        </div>

        {res && (
          <div className="mt-6 space-y-4" data-testid="wizard-result">
            <div className="border rounded-xl bg-[hsl(var(--card))] p-4" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="flex items-center gap-2">
                <span className="chip chip-accent">{res.role_label}</span>
                <span className="chip chip-info">{res.stage_label}</span>
                {res.qc_gate_ahead && <span className="chip chip-warning">{res.qc_gate_ahead}</span>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Section icon={CheckSquare} title="Responsibilities" items={res.responsibilities} />
                <Section icon={FileText} title="Deliverables" items={res.deliverables} />
                <Section icon={FileText} title="Templates to use" items={res.templates} />
                <div>
                  <div className="flex items-center gap-2 mb-2"><Shield size={14} /><div className="font-heading font-semibold">Communication rule</div></div>
                  <p className="text-[13px] text-[hsl(var(--foreground-muted))] leading-relaxed">{res.communication_rule}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

const Section = ({ icon: Icon, title, items }) => (
  <div>
    <div className="flex items-center gap-2 mb-2"><Icon size={14} /><div className="font-heading font-semibold">{title}</div></div>
    {!items?.length ? <div className="text-[13px] text-[hsl(var(--foreground-subtle))]">—</div> :
      <ul className="text-[13.5px] pl-4 list-disc space-y-1">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>}
  </div>
);
